import { useRef, useEffect, useCallback } from 'react';

/**
 * Track mouse position across the viewport without causing re-renders.
 *
 * Returns two refs:
 *   normalizedPos  – { x: -1..1, y: -1..1 } relative to viewport center (0,0 = center)
 *   screenPos      – { x, y } in raw pixels (clientX / clientY)
 *
 * Updates are throttled to ~60 fps via requestAnimationFrame.
 * Uses refs (not state) so consumers must read `.current` inside
 * their own rAF / useFrame loops.
 */
export default function useMousePosition() {
  const normalizedRef = useRef({ x: 0, y: 0 });
  const screenRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const handlePointerMove = useCallback((e) => {
    if (rafRef.current) return; // throttle to 1 update per frame
    rafRef.current = requestAnimationFrame(() => {
      screenRef.current = { x: e.clientX, y: e.clientY };
      normalizedRef.current = {
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      };
      rafRef.current = null;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handlePointerMove]);

  return { normalizedPos: normalizedRef, screenPos: screenRef };
}

export { useMousePosition };
